
import { defineComponent, PropType } from 'vue'
import {
  formatRelativeTime,
  isOutgoingRole,
  linkifyText
} from '@/utils/inboxMessageFormat.js'

interface InboxAttachment {
  url: string
  mimeType?: string
  filename?: string
}

interface InboxMessage {
  role: string
  content: string
  attachments?: InboxAttachment[]
  createdAt?: string | null
}

export default defineComponent({
  name: 'inbox-message-bubble',
  emits: ['content-loaded'],
  props: {
    message: {
      type: Object as PropType<InboxMessage>,
      required: true
    },
    contactName: {
      type: String,
      default: 'Contact'
    },
    assistantLabel: {
      type: String,
      default: 'AI'
    }
  },
  computed: {
    isOutgoing(): boolean {
      return isOutgoingRole(this.message.role)
    },
    senderLabel(): string {
      if (this.message.role === 'assistant') {
        return this.assistantLabel
      }
      if (this.message.role === 'human') {
        return 'You'
      }
      return this.contactName
    },
    timeLabel(): string {
      return formatRelativeTime(this.message.createdAt) || ''
    },
    bubbleClass(): string {
      if (this.isOutgoing) {
        return 'bg-light-primary text-gray-900 text-end'
      }
      return 'bg-light-secondary text-gray-900 text-start'
    },
    attachmentItems(): InboxAttachment[] {
      return Array.isArray(this.message.attachments) ? this.message.attachments : []
    },
    hasTextContent(): boolean {
      const text = String(this.message.content || '').trim()
      if (!text || text === ' ') {
        return false
      }
      if (text === '[image attachment]') {
        return this.attachmentItems.length === 0
      }
      return true
    },
    formattedContent(): string {
      return linkifyText(this.message.content || '')
    }
  },
  methods: {
    isImageAttachment(attachment: InboxAttachment): boolean {
      const mime = String(attachment.mimeType || '').toLowerCase()
      if (mime.startsWith('image/')) {
        return true
      }
      return /\.(jpe?g|png|gif|webp)(\?|$)/i.test(String(attachment.url || ''))
    },
    isVideoAttachment(attachment: InboxAttachment): boolean {
      const mime = String(attachment.mimeType || '').toLowerCase()
      if (mime.startsWith('video/')) {
        return true
      }
      return /\.(mp4|webm|mov)(\?|$)/i.test(String(attachment.url || ''))
    }
  }
})
